import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import { Copy, Layers } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('portalToken')}` });

const formatDate = (value) =>
  new Date(value).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });

/**
 * Choix d'un template existant comme point de départ.
 *
 * Le template choisi est chargé dans l'état de travail du constructeur : rien
 * n'est modifié en base tant que l'utilisateur n'enregistre pas, donc partir
 * d'un template ne risque jamais d'abîmer l'original.
 */
function TemplateStarterDialog({ open, onClose, projectTypeId, onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setSelected(null); setError(''); return; }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/ai/template/existing${projectTypeId ? `?projectTypeId=${projectTypeId}` : ''}`,
          { headers: authHeaders() }
        );
        if (!res.ok) throw new Error('Chargement des templates impossible');
        const data = await res.json();
        if (!cancelled) setTemplates(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [open, projectTypeId]);

  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    setError('');
    try {
      const res = await fetch(`/api/ai/template/${selected.id}/state`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chargement impossible');
      onSelect({ ...data, sourceName: selected.name });
    } catch (err) {
      setError(err.message);
    }
    setApplying(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !applying && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Copy size={18} />
        Partir d'un template existant
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Le template choisi sert de base de travail. L'original n'est pas modifié :
          vous enregistrerez une nouvelle version à la fin.
        </Typography>

        {error && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : templates.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            Aucun template pour ce type de projet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {templates.map((template) => (
              <ListItemButton
                key={template.id}
                selected={selected?.id === template.id}
                onClick={() => setSelected(template)}
                sx={{
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  mb: 1,
                  display: 'block',
                  bgcolor: selected?.id === template.id ? 'rgba(0,212,170,0.06)' : 'rgba(255,255,255,0.02)'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{template.name}</Typography>
                {template.description && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {template.description}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip icon={<Layers size={11} />} label={`${template.sectionCount} sections`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  <Chip label={`${template.itemCount} items`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  {template.usageCount > 0 && (
                    <Chip label={`utilisé ${template.usageCount}×`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                  <Chip label={formatDate(template.updatedAt)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                </Stack>
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={applying}>Annuler</Button>
        <Button variant="contained" onClick={apply} disabled={!selected || applying}>
          {applying ? 'Chargement...' : 'Partir de ce template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateStarterDialog;
